// src/components/auth/AuthWrapper.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoginPage from './LoginPage';
import AuthContext from './AuthContext';
import config from '@/config.js';
import { createLogger } from '../../utils/logger';

const logger = createLogger('AUTH');

// URLs del backend y UAU
const API_URL = config.API_BASE_URL;

const AuthWrapper = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isAppAuthorized, setIsAppAuthorized] = useState(false);

  // El acceso se determina por tener organizations asignadas a esta app en UAU
  const hasAppAccess = (userInfo) => {
    return Array.isArray(userInfo?.organizations) && userInfo.organizations.length > 0;
  };

  // Obtener datos enriquecidos del usuario (con organizations) desde el backend
  const fetchUserInfo = async (token) => {
    const response = await axios.get(`${API_URL}/auth/user-info`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  // Verificar autenticación al iniciar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await fetchUserInfo(token);
        setIsAuthenticated(true);
        setUser(userData);

        const hasAccess = hasAppAccess(userData);
        setIsAppAuthorized(hasAccess);
        if (!hasAccess) {
          setError('No tienes acceso a esta aplicación');
        }
      } catch (error) {
        logger.debug('Error al validar token:', error);
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Función para manejar el login (via backend, que enriquece con organizations)
  const handleLogin = async (credentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username: credentials.username,
        password: credentials.password
      });

      if (response.data.access_token) {
        const token = response.data.access_token;
        const userInfo = response.data.user_info;

        const hasAccess = hasAppAccess(userInfo);
        if (!hasAccess) {
          setError('No tienes acceso a esta aplicación');
          return;
        }

        localStorage.setItem('token', token);
        setUser(userInfo);
        setIsAuthenticated(true);
        setIsAppAuthorized(true);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object') {
        setError(detail.message || 'Usuario o contraseña incorrectos');
      } else {
        setError(detail || 'Usuario o contraseña incorrectos');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar sesión
  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setIsAppAuthorized(false);
    setUser(null);
  };

  // Mostrar pantalla de carga
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Cargando...</p>
      </div>
    );
  }

  // Mostrar pantalla de login si no está autenticado
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} error={error} />;
  }

  // Mostrar mensaje de error si está autenticado pero no tiene acceso a la aplicación
  if (!isAppAuthorized) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2 style={{ color: '#d32f2f' }}>Acceso Denegado</h2>
        <p>No tienes permiso para acceder a esta aplicación. Por favor contacta al administrador.</p>
        <button
          onClick={handleLogout}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Cerrar sesión
        </button>
      </div>
    );
  }

  // Proporcionar el contexto de autenticación a los componentes hijos
  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAppAuthorized,
      user,
      logout: handleLogout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthWrapper;
